import { normalizeResourceId } from '../resources/resources';

import { createApiClient } from './client';
import type { GuestAccessCommand, GuestAccessRuleDto } from './guestAccessTypes';
import type {
  MatchingBoardPageDto,
  MatchingEventViewDto,
  MatchingMetaDto,
  MatchingPanelDayDto,
  MatchingRequestCommand,
  MatchingRequestDto,
  MatchingRestoreDto,
  MatchingStatusDto,
  MatchingWindowDto,
} from './matchingTypes';
import type { NotificationPreferencesDto } from './notificationTypes';
import type {
  AdminSetRoleRequest,
  AdminSetRoleResponse,
  AdminUsersListResponse,
  AuthTelegramResponse,
  EventActionResponse,
  EventCancelResponse,
  EventCreateRequest,
  EventDto,
  EventUpdateRequest,
  EventsListResponse,
  GameCreateRequest,
  GameDto,
  GameImportDto,
  GameTagDto,
  GameUpdateRequest,
  GamesListResponse,
  GroupsListResponse,
  GroupCreateRequest,
  GroupUpdateRequest,
  GroupDto,
  LoginChallenge,
  TelegramWidgetUser,
  UsersListResponse,
  UserDto,
  UpdateClubKeyRequest,
} from './types';

type BackendRole = 'ROLE_USER' | 'ROLE_ORGANIZER' | 'ROLE_ADMIN';
type BackendParticipantStatus = 'PENDING' | 'CONFIRMED';

type BackendUserDetails = {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  photoUrl: string | null;
  role: BackendRole;
  hasClubKey: boolean;
  createdAt: string;
  updatedAt: string;
};

type BackendEventParticipant = {
  id: number;
  user: BackendUserDetails;
  status: BackendParticipantStatus;
  updatedAt: string;
};

type BackendEvent = {
  id: number;
  title: string;
  type: 'BOARDGAMES' | 'PING_PONG' | 'FENCING' | 'ARCHERY' | 'HIKING' | 'COURTYARD';
  visibility: 'OPEN' | 'PRIVATE';
  resourceId: string;
  createdByUser: BackendUserDetails;
  maxParticipants: number;
  startAt: string;
  endAt: string;
  createdAt: string;
  updatedAt: string;
  eventParticipants?: BackendEventParticipant[];
  game?: { id: number; name: string } | null;
  description?: string | null;
};

type BackendGroupParticipant = {
  id: number;
  user: BackendUserDetails;
  status: BackendParticipantStatus;
  updatedAt: string;
};

type BackendGroup = {
  id: number;
  name: string;
  description: string;
  visibility: 'OPEN' | 'PRIVATE';
  createdByUser: BackendUserDetails;
  createdAt: string;
  updatedAt: string;
  groupParticipants?: BackendGroupParticipant[];
};

type BackendGameTag = {
  id: number;
  name: string;
};

type BackendGame = {
  id: number;
  name: string;
  description: string | null;
  imageUrl: string | null;
  gameUrl: string | null;
  minPlayers: number;
  maxPlayers: number;
  estimatedDuration: number | null;
  tags: BackendGameTag[];
  createdAt: string;
  updatedAt: string;
};

function mapRole(role: BackendRole): 'user' | 'organizer' | 'admin' {
  if (role === 'ROLE_ADMIN') return 'admin';
  if (role === 'ROLE_ORGANIZER') return 'organizer';
  return 'user';
}

function mapEventType(type: BackendEvent['type']): EventDto['type'] {
  switch (type) {
    case 'PING_PONG':
      return 'ping_pong';
    case 'FENCING':
      return 'fencing';
    case 'ARCHERY':
      return 'archery';
    case 'HIKING':
      return 'hiking';
    case 'COURTYARD':
      return 'courtyard';
    case 'BOARDGAMES':
      return 'boardgames';
    default:
      console.warn('Unknown backend event type, fallback to boardgames:', type);
      return 'boardgames';
  }
}

function mapBackendEventType(
  type: EventCreateRequest['type'] | EventUpdateRequest['type'],
): BackendEvent['type'] {
  if (type === 'ping_pong') return 'PING_PONG';
  if (type === 'fencing') return 'FENCING';
  if (type === 'archery') return 'ARCHERY';
  if (type === 'hiking') return 'HIKING';
  if (type === 'courtyard') return 'COURTYARD';
  return 'BOARDGAMES';
}

function mapVisibility(v: BackendEvent['visibility']): EventDto['visibility'] {
  return v === 'PRIVATE' ? 'private' : 'open';
}

function mapParticipantStatus(s: BackendParticipantStatus): 'pending' | 'confirmed' {
  return s === 'CONFIRMED' ? 'confirmed' : 'pending';
}

function mapEvent(e: BackendEvent): EventDto {
  const maxParticipants = e.maxParticipants ?? 0;

  // Map backend participants to frontend ParticipantDto[] (no slots, just list)
  const backendParticipants = [...(e.eventParticipants ?? [])].sort((a, b) => a.id - b.id);
  const participants: EventDto['participants'] = backendParticipants.map((p) => ({
    telegramId: String(p.user.id),
    status: mapParticipantStatus(p.status),
  }));

  return {
    id: String(e.id),
    title: e.title,
    type: mapEventType(e.type),
    visibility: mapVisibility(e.visibility),
    resourceId: normalizeResourceId(e.resourceId),
    createdByTelegramId: String(e.createdByUser?.id ?? ''),
    maxParticipants,
    startAt: e.startAt,
    endAt: e.endAt,
    status: 'active',
    updatedAt: e.updatedAt,
    participants,
    game: e.game ? { id: String(e.game.id), name: e.game.name } : null,
    description: e.description ?? null,
  };
}

function mapGroup(g: BackendGroup): GroupDto {
  const members = (g.groupParticipants ?? [])
    .slice()
    .sort((a, b) => a.id - b.id)
    .map((p) => ({
      telegramId: String(p.user.id),
      status: mapParticipantStatus(p.status),
    }));

  return {
    id: String(g.id),
    name: g.name,
    description: g.description,
    visibility: g.visibility === 'PRIVATE' ? 'private' : 'open',
    createdByTelegramId: String(g.createdByUser?.id ?? ''),
    updatedAt: g.updatedAt,
    members,
  };
}

function mapGame(g: BackendGame): GameDto {
  return {
    id: String(g.id),
    name: g.name,
    description: g.description,
    imageUrl: g.imageUrl,
    gameUrl: g.gameUrl,
    minPlayers: g.minPlayers,
    maxPlayers: g.maxPlayers,
    estimatedDuration: g.estimatedDuration,
    tags: (g.tags ?? []).map((t) => ({ id: String(t.id), name: t.name })),
    createdAt: g.createdAt,
    updatedAt: g.updatedAt,
  };
}

function mapUser(user: BackendUserDetails): UserDto {
  return {
    telegramId: String(user.id),
    firstName: user.firstName,
    lastName: user.lastName,
    username: user.username,
    photoUrl: user.photoUrl ?? undefined,
    role: mapRole(user.role),
    hasClubKey: user.hasClubKey,
  };
}

function mapAuthResponse(res: BackendUserDetails): AuthTelegramResponse {
  return {
    user: {
      telegramId: String(res.id),
      first_name: res.firstName,
      last_name: res.lastName,
      username: res.username,
      photo_url: res.photoUrl ?? undefined,
      role: mapRole(res.role as BackendRole),
      hasClubKey: res.hasClubKey,
    },
  };
}

export function createEndpoints(params: {
  baseUrl?: string;
  getInitData?: () => string | null;
  onForbidden?: () => void;
}) {
  const api = createApiClient({
    baseUrl: params.baseUrl,
    getInitData: params.getInitData,
    onForbidden: params.onForbidden,
  });

  return {
    authTelegram: async (initData: string) => {
      // Backend expects raw initData string (not JSON-encoded).
      // Cookie is set automatically by the response. Body contains UserDto.
      const res = await api.postRaw<BackendUserDetails>('/auth/telegram', initData);
      return mapAuthResponse(res);
    },

    /** Browser login: Telegram Login Widget payload. Sets the auth cookie. */
    authTelegramWidget: async (data: TelegramWidgetUser) => {
      const res = await api.post<BackendUserDetails>('/auth/telegram-widget', data);
      return mapAuthResponse(res);
    },

    /** Restores the session from the auth cookie (browser, no initData). */
    getMe: async () => {
      const res = await api.get<BackendUserDetails>('/users/me');
      return mapAuthResponse(res);
    },

    /** Browser login via bot: creates a one-time code + t.me deep-link for a QR. */
    createLoginChallenge: () => api.post<LoginChallenge>('/auth/browser/challenge'),

    /**
     * Polls the challenge. Resolves to `null` while the user has not confirmed in the bot yet,
     * to the session once confirmed (cookie is set); rejects with HttpError 410 when expired.
     */
    pollLoginChallenge: async (code: string) => {
      const res = await api.postRaw<BackendUserDetails | undefined>('/auth/browser/challenge/status', code);
      return res ? mapAuthResponse(res) : null;
    },

    authLogout: async () => {
      // Sends DELETE /auth/logout to clear the cookie.
      // Backend returns 204 No Content.
      await api.del<void>('/auth/logout');
    },

    getEvent: async (eventId: string) => {
      const res = await api.get<BackendEvent>(`/event/${encodeURIComponent(eventId)}`);
      return mapEvent(res);
    },

    listEventsRange: async (from: string, to: string) => {
      const res = await api.get<BackendEvent[]>(
        `/event?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      );

      const mapped: EventsListResponse = { items: res.map(mapEvent) };
      return mapped;
    },

    listEventsDate: async (date: string) => {
      const res = await api.get<BackendEvent[]>(`/event?date=${encodeURIComponent(date)}`);
      const mapped: EventsListResponse = { items: res.map(mapEvent) };
      return mapped;
    },

    listGroups: async () => {
      const res = await api.get<BackendGroup[]>('/group');
      const mapped: GroupsListResponse = { items: res.map(mapGroup) };
      return mapped;
    },

    listUsers: async () => {
      const res = await api.get<BackendUserDetails[]>('/users');
      const mapped: UsersListResponse = {
        items: res.map(mapUser),
      };
      return mapped;
    },

    listUsersByRole: async (role: 'user' | 'organizer' | 'admin') => {
      const backendRole =
        role === 'admin' ? 'ROLE_ADMIN' : role === 'organizer' ? 'ROLE_ORGANIZER' : 'ROLE_USER';
      const res = await api.get<BackendUserDetails[]>(`/users/role/${backendRole}`);
      const mapped: UsersListResponse = {
        items: res.map(mapUser),
      };
      return mapped;
    },

    updateUserRole: async (userId: string, role: 'user' | 'organizer' | 'admin') => {
      const backendRole =
        role === 'admin' ? 'ROLE_ADMIN' : role === 'organizer' ? 'ROLE_ORGANIZER' : 'ROLE_USER';
      const res = await api.post<BackendUserDetails>(
        `/users/${encodeURIComponent(userId)}/role`,
        backendRole,
      );
      return mapUser(res);
    },

    updateCurrentUserClubKey: async (payload: UpdateClubKeyRequest) => {
      const res = await api.patch<BackendUserDetails>('/users/me/club-key', payload);
      return mapUser(res);
    },

    createGroup: async (payload: GroupCreateRequest) => {
      const backendPayload = {
        name: payload.name,
        description: payload.description,
        visibility: payload.visibility === 'private' ? 'PRIVATE' : 'OPEN',
        groupParticipants:
          payload.participants?.map((p) => ({
            user: { id: Number(p.telegramId) },
          })) || [],
      };
      const res = await api.post<BackendGroup>('/group', backendPayload);
      return mapGroup(res);
    },

    createEvent: async (payload: EventCreateRequest) => {
      const backendPayload: Record<string, unknown> = {
        title: payload.title,
        type: mapBackendEventType(payload.type),
        visibility: payload.visibility === 'private' ? 'PRIVATE' : 'OPEN',
        resourceId: normalizeResourceId(payload.resourceId),
        maxParticipants: payload.maxParticipants,
        startAt: payload.startAt,
        endAt: payload.endAt,
        eventParticipants:
          payload.participants?.map((p) => ({
            user: { id: Number(p.telegramId) },
            ...(p.status === 'confirmed' ? { status: 'CONFIRMED' } : {}),
          })) || [],
      };

      if (payload.gameId) {
        backendPayload.game = { id: Number(payload.gameId) };
      }
      if (payload.description) {
        backendPayload.description = payload.description;
      }

      const res = await api.post<BackendEvent>('/event', backendPayload);
      return mapEvent(res);
    },

    updateEvent: async (eventId: string, payload: EventUpdateRequest) => {
      const backendPayload = {
        id: Number(eventId),
        title: payload.title,
        type: mapBackendEventType(payload.type),
        visibility: payload.visibility === 'private' ? 'PRIVATE' : 'OPEN',
        resourceId: normalizeResourceId(payload.resourceId),
        maxParticipants: payload.maxParticipants,
        startAt: payload.startAt,
        endAt: payload.endAt,
        description: payload.description ?? null,
      };

      const res = await api.patch<BackendEvent>('/event', backendPayload);
      return mapEvent(res);
    },

    cancelEvent: async (eventId: string) => {
      const deletedId = await api.del<number>(`/event/${encodeURIComponent(eventId)}`);
      // Backend returns empty body; keep frontend contract for state patch.
      const now = new Date().toISOString();
      const mapped: EventCancelResponse = {
        id: String(deletedId),
        status: 'cancelled',
        updatedAt: now,
      };
      return mapped;
    },

    // JOIN: Участник сразу принимает участие (status = confirmed)
    joinEvent: async (eventId: string) => {
      const res = await api.post<BackendEvent>(
        `/event/${encodeURIComponent(eventId)}/join`,
        undefined,
      );
      const event = mapEvent(res);
      const mapped: EventActionResponse = {
        event: {
          id: event.id,
          updatedAt: event.updatedAt,
          status: event.status,
          title: event.title,
          startAt: event.startAt,
          endAt: event.endAt,
          maxParticipants: event.maxParticipants,
          visibility: event.visibility,
          participants: event.participants,
        },
      };
      return mapped;
    },

    leaveEvent: async (eventId: string) => {
      const res = await api.post<BackendEvent>(
        `/event/${encodeURIComponent(eventId)}/leave`,
        undefined,
      );
      const event = mapEvent(res);
      const mapped: EventActionResponse = {
        event: {
          id: event.id,
          updatedAt: event.updatedAt,
          status: event.status,
          title: event.title,
          startAt: event.startAt,
          endAt: event.endAt,
          maxParticipants: event.maxParticipants,
          visibility: event.visibility,
          participants: event.participants,
        },
      };
      return mapped;
    },

    // CONFIRM: Подтверждение приглашения (pending → confirmed)

    confirmEvent: async (eventId: string) => {
      const res = await api.post<BackendEvent>(
        `/event/${encodeURIComponent(eventId)}/confirm`,
        undefined,
      );
      const event = mapEvent(res);
      const mapped: EventActionResponse = {
        event: {
          id: event.id,
          updatedAt: event.updatedAt,
          status: event.status,
          title: event.title,
          startAt: event.startAt,
          endAt: event.endAt,
          maxParticipants: event.maxParticipants,
          visibility: event.visibility,
          participants: event.participants,
        },
      };
      return mapped;
    },

    // INVITE: Приглашение участников (status = pending, ожидание подтверждения)
    inviteToEvent: async (eventId: string, telegramIds: number[]) => {
      const res = await api.post<BackendEvent>(
        `/event/${encodeURIComponent(eventId)}/invite`,
        telegramIds,
      );
      return mapEvent(res);
    },

    // INVITE GROUP: Приглашение всего отряда (status = pending для всех участников)
    inviteGroupToEvent: async (eventId: string, groupId: string) => {
      const res = await api.post<BackendEvent>(
        `/event/${encodeURIComponent(eventId)}/invite/group/${encodeURIComponent(groupId)}`,
        undefined,
      );
      return mapEvent(res);
    },

    removeFromEvent: async (eventId: string, telegramIds: number[]) => {
      const res = await api.post<BackendEvent>(
        `/event/${encodeURIComponent(eventId)}/leave`,
        telegramIds,
      );
      return mapEvent(res);
    },

    unreserveFromEvent: async (eventId: string, telegramIds: number[]) => {
      const res = await api.post<BackendEvent>(
        `/event/${encodeURIComponent(eventId)}/unreserve`,
        telegramIds,
      );
      return mapEvent(res);
    },

    inviteToGroup: async (groupId: string, telegramIds: number[]) => {
      const res = await api.post<BackendGroup>(
        `/group/${encodeURIComponent(groupId)}/invite`,
        telegramIds,
      );
      return mapGroup(res);
    },

    removeFromGroup: async (groupId: string, telegramIds: number[]) => {
      const res = await api.post<BackendGroup>(
        `/group/${encodeURIComponent(groupId)}/leave`,
        telegramIds,
      );
      return mapGroup(res);
    },

    confirmGroup: async (groupId: string) => {
      const res = await api.post<BackendGroup>(
        `/group/${encodeURIComponent(groupId)}/confirm`,
        undefined,
      );
      return mapGroup(res);
    },

    leaveGroup: async (groupId: string) => {
      const res = await api.post<BackendGroup>(
        `/group/${encodeURIComponent(groupId)}/leave`,
        undefined,
      );
      return mapGroup(res);
    },

    joinGroup: async (groupId: string) => {
      const res = await api.post<BackendGroup>(
        `/group/${encodeURIComponent(groupId)}/join`,
        undefined,
      );
      return mapGroup(res);
    },

    updateGroup: async (groupId: string, payload: GroupUpdateRequest) => {
      const backendPayload = {
        id: Number(groupId),
        name: payload.name,
        description: payload.description,
        visibility: payload.visibility === 'private' ? 'PRIVATE' : 'OPEN',
      };
      const res = await api.patch<BackendGroup>('/group', backendPayload);
      return mapGroup(res);
    },

    deleteGroup: async (groupId: string): Promise<string> => {
      const deletedId = await api.del<number>(`/group/${encodeURIComponent(groupId)}`);
      return String(deletedId);
    },

    // --- Games ---

    listGames: async (params?: { name?: string; tags?: string[] }) => {
      const searchParams = new URLSearchParams();
      if (params?.name) searchParams.set('name', params.name);
      if (params?.tags) params.tags.forEach((t) => searchParams.append('tags', t));
      const query = searchParams.toString();
      const path = query ? `/game?${query}` : '/game';
      const res = await api.get<BackendGame[]>(path);
      return { items: res.map(mapGame) } as GamesListResponse;
    },

    getGame: async (gameId: string) => {
      const res = await api.get<BackendGame>(`/game/${encodeURIComponent(gameId)}`);
      return mapGame(res);
    },

    createGame: async (payload: GameCreateRequest) => {
      const backendPayload = {
        name: payload.name,
        description: payload.description ?? null,
        imageUrl: payload.imageUrl ?? null,
        gameUrl: payload.gameUrl ?? null,
        minPlayers: payload.minPlayers,
        maxPlayers: payload.maxPlayers,
        estimatedDuration: payload.estimatedDuration ?? null,
        tags: payload.tags.map((name) => ({ name: name.toLowerCase().trim() })),
      };
      const res = await api.post<BackendGame>('/game', backendPayload);
      return mapGame(res);
    },

    updateGame: async (gameId: string, payload: GameUpdateRequest) => {
      const backendPayload: Record<string, unknown> = {};
      if (payload.name !== undefined) backendPayload.name = payload.name;
      if (payload.description !== undefined) backendPayload.description = payload.description;
      if (payload.imageUrl !== undefined) backendPayload.imageUrl = payload.imageUrl;
      if (payload.gameUrl !== undefined) backendPayload.gameUrl = payload.gameUrl;
      if (payload.minPlayers !== undefined) backendPayload.minPlayers = payload.minPlayers;
      if (payload.maxPlayers !== undefined) backendPayload.maxPlayers = payload.maxPlayers;
      if (payload.estimatedDuration !== undefined)
        backendPayload.estimatedDuration = payload.estimatedDuration;
      if (payload.tags !== undefined) {
        backendPayload.tags = payload.tags.map((name) => ({ name: name.toLowerCase().trim() }));
      }
      const res = await api.patch<BackendGame>(
        `/game/${encodeURIComponent(gameId)}`,
        backendPayload,
      );
      return mapGame(res);
    },

    deleteGame: async (gameId: string) => {
      await api.del<number>(`/game/${encodeURIComponent(gameId)}`);
    },

    importGame: async (source: string, identifier: string) => {
      return api.get<GameImportDto>(
        `/game/import/${encodeURIComponent(source)}?identifier=${encodeURIComponent(identifier)}`,
      );
    },

    listGameTags: async () => {
      const res = await api.get<BackendGameTag[]>('/game/tags');
      return res.map((t) => ({ id: String(t.id), name: t.name })) as GameTagDto[];
    },

    searchGameTags: async (query: string) => {
      const res = await api.get<BackendGameTag[]>(
        `/game/tags/search?q=${encodeURIComponent(query)}`,
      );
      return res.map((t) => ({ id: String(t.id), name: t.name })) as GameTagDto[];
    },

    getFavoriteGames: async () => {
      const res = await api.get<BackendGame[]>('/game/favorite');
      return { items: res.map(mapGame) } as GamesListResponse;
    },

    isGameFavorite: async (gameId: string) => {
      return api.get<boolean>(`/game/${encodeURIComponent(gameId)}/favorite`);
    },

    addGameFavorite: async (gameId: string) => {
      await api.post<void>(`/game/${encodeURIComponent(gameId)}/favorite`, undefined);
    },

    removeGameFavorite: async (gameId: string) => {
      await api.del<void>(`/game/${encodeURIComponent(gameId)}/favorite`);
    },

    // ---- Guest access ----

    getGuestAccessRules: () => api.get<GuestAccessRuleDto[]>('/guest-access'),

    getActiveGuestAccess: () => api.get<GuestAccessRuleDto[]>('/guest-access/active'),

    getGuestAccessBusyResources: (startAt: string, endAt: string) =>
      api.get<string[]>(
        `/guest-access/busy?startAt=${encodeURIComponent(startAt)}&endAt=${encodeURIComponent(endAt)}`,
      ),

    createGuestAccessRule: (command: GuestAccessCommand) =>
      api.post<GuestAccessRuleDto>('/guest-access', command),

    updateGuestAccessRule: (id: number, command: GuestAccessCommand) =>
      api.put<GuestAccessRuleDto>(`/guest-access/${id}`, command),

    deleteGuestAccessRule: (id: number) => api.del<number>(`/guest-access/${id}`),

    // ---- Notification preferences ----

    getNotificationPreferences: () =>
      api.get<NotificationPreferencesDto>('/notifications/preferences'),

    setAllNotifications: (enabled: boolean) =>
      api.put<NotificationPreferencesDto>('/notifications/preferences', { enabled }),

    setNotificationCategory: (category: string, enabled: boolean) =>
      api.put<NotificationPreferencesDto>(`/notifications/preferences/categories/${category}`, {
        enabled,
      }),

    setNotificationType: (type: string, enabled: boolean) =>
      api.put<NotificationPreferencesDto>(`/notifications/preferences/${type}`, { enabled }),

    // ---- Matching ("Сбор") ----

    getMatchingStatus: () => api.get<MatchingStatusDto>('/matching/status'),

    setMatchingStatus: (enabled: boolean) =>
      api.put<MatchingStatusDto>('/matching/status', { enabled }),

    getMatchingMeta: () => api.get<MatchingMetaDto>('/matching/meta'),

    previewMatchingRequest: (command: MatchingRequestCommand) =>
      api.post<MatchingWindowDto>('/matching/requests/preview', command),

    createMatchingRequest: (command: MatchingRequestCommand) =>
      api.post<MatchingRequestDto>('/matching/requests', command),

    updateMatchingRequest: (id: number, version: number, command: MatchingRequestCommand) =>
      api.patch<MatchingRequestDto>(`/matching/requests/${id}`, command, {
        'If-Match': `"${version}"`,
      }),

    cancelMatchingRequest: (id: number) => api.del<void>(`/matching/requests/${id}`),

    copyMatchingRequest: (id: number) =>
      api.post<MatchingRequestDto>(`/matching/requests/${id}/copy`, undefined),

    restoreMatchingRequest: (id: number) =>
      api.post<MatchingRestoreDto>(`/matching/requests/${id}/restore`, undefined),

    getMatchingBoard: (params: { from?: string; to?: string; page: number; size: number }) => {
      const q = new URLSearchParams();
      if (params.from) q.set('from', params.from);
      if (params.to) q.set('to', params.to);
      q.set('page', String(params.page));
      q.set('size', String(params.size));
      return api.get<MatchingBoardPageDto>(`/matching/requests?${q.toString()}`);
    },

    getMatchingPanel: (params: { from?: string; to?: string }) => {
      const q = new URLSearchParams();
      if (params.from) q.set('from', params.from);
      if (params.to) q.set('to', params.to);
      return api.get<MatchingPanelDayDto[]>(`/matching/panel?${q.toString()}`);
    },

    getMatchingEventViews: (eventIds: number[]) =>
      api.get<MatchingEventViewDto[]>(`/matching/events?eventIds=${eventIds.join(',')}`),

    adminListUsers: async (_params?: { query?: string; limit?: number; offset?: number }) => {
      // Not implemented in current backend (see docs/development/backend/backend-requirements.md).
      throw new Error('Admin API is not implemented on backend yet');
    },

    adminSetUserRole: async (_telegramId: string, _payload: AdminSetRoleRequest) => {
      // Not implemented in current backend (see docs/development/backend/backend-requirements.md).
      throw new Error('Admin API is not implemented on backend yet');
    },
  };
}
